import React, { useState, useEffect } from 'react';
import { FaArrowLeft } from 'react-icons/fa';
import './ProjectDetails.scss';
import '../../../styles/GenericSiteStyles.scss';
import FormGenerator, { FormFieldConfig } from '../../common/FormGenerator/FormGenerator';
import GenericFlyoutPanel from '../../common/GenericFlyoutPanel/GenericFlyoutPanel';
import ProjectGeneral from './ProjectGeneral';
import ProjectRisks from './ProjectRisks';
import ProjectPlanning from './ProjectPlanning';
import ProjectEstimate from './ProjectEstimate';
import ProjectPricing from './ProjectPricing';
import ProjectCosts from './ProjectCosts';
import ProjectTimeWriting from './ProjectTimeWriting';
import GenericVerticalTabs, { TabConfig } from '../../common/GenericVerticalTabs/GenericVerticalTabs';
import Breadcrumb from '../../common/Breadcrumb/Breadcrumb';
import MainScreenHeader from '../../common/Header/MainScreenHeader';
import '../../common/Breadcrumb/Breadcrumb.scss';
import { ProjectsRestDataProvider, Project as ApiProject } from '../../../data/ProjectsRestDataProvider';
import { useParams, useNavigate } from 'react-router-dom';
import { UserRoles } from '../../../constants/UserRoles';
import { ProjectScenariosProvider, useProjectScenarios } from '../../../context/ProjectScenariosContext';
import { getProjectFields } from './Projects';
import { useTranslation } from 'react-i18next';


const ProjectDetails: React.FC = () => {
  const { t } = useTranslation(['common', 'screens']);
  const { id } = useParams();
  const navigate = useNavigate();
  const projectId = id as string;
  const [showEdit, setShowEdit] = useState(false);
  const [projectData, setProjectData] = useState<ApiProject | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scenariosKey, setScenariosKey] = useState(0);
  const [scenarioId, setScenarioId] = useState<string>('');
  const [activeTab, setActiveTab] = useState('general');

  const provider = React.useMemo(() => new ProjectsRestDataProvider(), []);
  const fields = React.useMemo(() => getProjectFields(t), [t]);

  // Only show generic info fields
  const infoFields = React.useMemo(() => [
    { label: t('screens:projects.infoFields.description'), value: projectData?.description || '—' },
    { label: t('screens:projects.infoFields.status'), value: <span className={`badge status-badge status-${projectData?.status?.toLowerCase()}`}>{projectData?.status}</span> },
    { label: t('screens:projects.infoFields.startDate'), value: projectData?.startDate || '—' },
    { label: t('screens:projects.infoFields.endDate'), value: projectData?.endDate || '—' },
    { label: t('screens:projects.infoFields.budget'), value: projectData?.budget != null ? `€${projectData.budget.toLocaleString()}` : '—' },
    { label: t('screens:projects.infoFields.isFixedPrice'), value: projectData?.isFixedPrice != null ? (projectData.isFixedPrice ? t('common:yes') : t('common:no')) : '—' },
    { label: t('screens:projects.infoFields.contingency'), value: projectData?.fixedPriceContingencyPercentage != null ? `${projectData.fixedPriceContingencyPercentage}%` : '—' },
    { label: t('screens:projects.infoFields.vat'), value: projectData?.vatPercentage != null ? `${projectData.vatPercentage}%` : '—' },
  ], [projectData, t]);

  // Dirty state handler for tabs
  const [tabDirtyMap, setTabDirtyMap] = useState<{ [tabKey: string]: boolean }>({});
  const handleDirtyChange = React.useCallback((tabKey: string, isDirty: boolean) => {
    setTabDirtyMap(prev => ({ ...prev, [tabKey]: isDirty }));
  }, []);

  const tabs: TabConfig[] = React.useMemo(() => [
    {
      key: 'general',
      label: t('screens:projects.tabs.general'),
      content: <ProjectGeneral project={projectData} infoFields={infoFields} onEdit={() => setShowEdit(true)} onScenarioCreated={() => setScenariosKey(k => k + 1)} />,
    },
    {
      key: 'requirements',
      label: t('screens:projects.tabs.requirements'),
      content: scenarioId ? React.createElement(require('./ProjectRequirements').default, { scenarioId, setScenarioId, onDirtyChange: (dirty: boolean) => handleDirtyChange('requirements', dirty) }) : null,
    },
    {
      key: 'assumptions',
      label: t('screens:projects.tabs.assumptions'),
      content: scenarioId ? React.createElement(require('./ProjectAssumptions').default, { scenarioId, setScenarioId, onDirtyChange: (dirty: boolean) => handleDirtyChange('assumptions', dirty) }) : null,
    },
    {
      key: 'estimate',
      label: t('screens:projects.tabs.estimate'),
      content: <ProjectEstimate scenarioId={scenarioId} setScenarioId={setScenarioId} onDirtyChange={dirty => handleDirtyChange('estimate', dirty)} />,
    },
    {
      key: 'planning',
      label: t('screens:projects.tabs.planning'),
      content: <ProjectPlanning projectId={projectId} scenarioId={scenarioId} setScenarioId={setScenarioId} onDirtyChange={dirty => handleDirtyChange('planning', dirty)} />,
    },
    {
      key: 'risks',
      label: t('screens:projects.tabs.risks'),
      content: <ProjectRisks scenarioId={scenarioId} setScenarioId={setScenarioId} onDirtyChange={dirty => handleDirtyChange('risks', dirty)} />,
    },
    {
      key: 'costs',
      label: t('screens:projects.tabs.costs'),
      content: <ProjectCosts scenarioId={scenarioId} setScenarioId={setScenarioId} onDirtyChange={dirty => handleDirtyChange('costs', dirty)} />,
      allowedRoles: UserRoles.Admin + ',' + UserRoles.Manager,
    },
    {
      key: 'pricing',
      label: t('screens:projects.tabs.pricing'),
      content: <ProjectPricing scenarioId={scenarioId} setScenarioId={setScenarioId} project={projectData} onDirtyChange={dirty => handleDirtyChange('pricing', dirty)} />,
      allowedRoles: UserRoles.Admin + ',' + UserRoles.Manager,
    },
    {
      key: 'timewriting',
      label: t('screens:projects.tabs.timeWriting'),
      content: <ProjectTimeWriting projectId={projectId} />,
    },
  ], [projectData, infoFields, showEdit, scenariosKey, scenarioId, setScenarioId, projectId, handleDirtyChange, t]);

  useEffect(() => {
    setLoading(true);
    provider.getById(projectId)
      .then(data => setProjectData(data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) return <div style={{ padding: 32 }}>Loading...</div>;
  if (error) return <div style={{ color: 'red', padding: 32 }}>{error}</div>;
  if (!projectData) return <div style={{ padding: 32 }}>Project not found.</div>;

  return (
    <div className="fancy-details-bg project-details">
      <MainScreenHeader title={projectData.name} />
      <ProjectScenariosProvider key={scenariosKey} projectId={projectId}>
        <ProjectScenariosInit
          scenarioId={scenarioId}
          setScenarioId={setScenarioId}
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          showEdit={showEdit}
          setShowEdit={setShowEdit}
          projectData={projectData}
          setProjectData={setProjectData}
          provider={provider}
          error={error}
          setError={setError}
          setLoading={setLoading}
          tabDirtyMap={tabDirtyMap}
          setTabDirtyMap={setTabDirtyMap}
        />
      </ProjectScenariosProvider>
    </div>
  );

}

// Helper component to handle tab switching and scenario save only if dirty
const ProjectDetailsTabs: React.FC<{
  tabs: TabConfig[];
  scenarioId: string;
  setScenarioId: (id: string) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  tabDirtyMap: { [tabKey: string]: boolean };
  setTabDirtyMap: React.Dispatch<React.SetStateAction<{ [tabKey: string]: boolean }>>;
}> = ({ tabs, scenarioId, setScenarioId, activeTab, setActiveTab, tabDirtyMap, setTabDirtyMap }) => {
  const { scenarios } = useProjectScenarios();
  const [saving, setSaving] = useState(false);
  const provider = React.useRef(new (require('../../../data/ProjectScenariosDataProvider').ProjectsScenariosRestDataProvider)()).current;

  // Find the current scenario
  const scenario = scenarios.find(s => s.id === scenarioId);

  // Only save for these tabs
  const scenarioTabs = ['estimate', 'risks', 'planning', 'pricing', 'requirements', 'assumptions'];

  // Save scenario to backend
  const saveScenario = async () => {
    if (!scenario || !scenario.id) return;
    setSaving(true);
    try {
      await provider.update(scenario.id, scenario);
    } finally {
      setSaving(false);
    }
  };

  // Handle tab change
  const handleTabChange = async (prevTab: string, nextTab: string) => {
    if (scenarioTabs.includes(prevTab) && scenario && tabDirtyMap[prevTab]) {
      await saveScenario();
      setTabDirtyMap((prev: any) => ({ ...prev, [prevTab]: false }));
    }
    setActiveTab(nextTab);
  };

  return (
    <GenericVerticalTabs
      tabs={tabs}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      onTabChange={handleTabChange}
    />
  );
};

// Helper to set initial scenarioId and render tabs, and handle edit dialog
const ProjectScenariosInit: React.FC<{
  scenarioId: string;
  setScenarioId: (id: string) => void;
  tabs: TabConfig[];
  activeTab: string;
  setActiveTab: (tab: string) => void;
  showEdit: boolean;
  setShowEdit: React.Dispatch<React.SetStateAction<boolean>>;
  projectData: ApiProject | null;
  setProjectData: React.Dispatch<React.SetStateAction<ApiProject | null>>;
  provider: ProjectsRestDataProvider;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  tabDirtyMap: { [tabKey: string]: boolean };
  setTabDirtyMap: React.Dispatch<React.SetStateAction<{ [tabKey: string]: boolean }>>;
}> = ({ scenarioId, setScenarioId, tabs, activeTab, setActiveTab, showEdit, setShowEdit, projectData, setProjectData, provider, error, setError, setLoading, tabDirtyMap, setTabDirtyMap }) => {
  const { scenarios } = useProjectScenarios();
  React.useEffect(() => {
    if (!scenarioId && scenarios && scenarios.length > 0) {
      setScenarioId(scenarios[0].id || '');
    }
  }, [scenarioId, scenarios, setScenarioId]);

  const { t } = useTranslation(['common', 'screens']);
  if (!scenarios || scenarios.length === 0) {
    return <div style={{ padding: 32 }}>{t('screens:projects.noScenariosFound')}</div>;
  }
  if (!scenarioId) {
    return <div style={{ padding: 32 }}>{t('screens:projects.loadingScenarios')}</div>;
  }
  return (
    <>
      <ProjectDetailsTabs
        tabs={tabs}
        scenarioId={scenarioId}
        setScenarioId={setScenarioId}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabDirtyMap={tabDirtyMap}
        setTabDirtyMap={setTabDirtyMap}
      />
      {showEdit && projectData && (
        <GenericFlyoutPanel open={showEdit} onClose={() => setShowEdit(false)} title={t('screens:projects.editProjectDetails')}>
          <FormGenerator
            fields={getProjectFields(t)}
            initialData={projectData}
            onSubmit={async data => {
              setLoading(true);
              setError(null);
              try {
                await provider.update(projectData.id ?? '', data);
                setProjectData({ ...projectData, ...data });
                setShowEdit(false);
              } catch (e: any) {
                setError(e.message);
              } finally {
                setLoading(false);
              }
            }}
            submitLabel={t('common:save')}
          />
          {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
        </GenericFlyoutPanel>
      )}
    </>
  );
};

export default ProjectDetails;
